import { c as _c } from "react/compiler-runtime";
import { ExternalLink } from '@morpho-org/ui-v6/server';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, type ButtonProps } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { InternalLink } from '@/components/common/InternalLink';
import type { NavSegmentControlProps, NavSegmentOption } from './NavSegmentControl';

// NavSegmentControl normalizes `ghost` to `secondary`; inactive options stay ghost.

const SIZE_MAP: Record<NonNullable<NavSegmentControlProps<string>['size']>, ButtonProps['size']> = {
  default: 'default',
  medium: 'md'
};
interface NavSegmentButtonProps<T> {
  option: NavSegmentOption<T>;
  value: string;
  size: NonNullable<NavSegmentControlProps<string>['size']>;
  fullWidth: boolean;
  vertical?: boolean;
  onChange?: (value: T) => void;
}
export function NavSegmentButton(t0) {
  const $ = _c(50);
  const {
    option,
    value,
    size,
    fullWidth,
    vertical: t1,
    onChange
  } = t0;
  const vertical = t1 === undefined ? false : t1;
  const isActive = option.value === value;
  let t2;
  if ($[0] !== option.label || $[1] !== option.labelWidth || $[2] !== vertical) {
    t2 = option.labelWidth && !vertical ? <span data-label-width={true} className="inline-flex justify-center" style={{
      minWidth: option.labelWidth
    }}>{option.label}</span> : option.label;
    $[0] = option.label;
    $[1] = option.labelWidth;
    $[2] = vertical;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const label = t2;
  let t3;
  if ($[4] !== label || $[5] !== option.counter) {
    t3 = option.counter && option.counter > 0 ? <span className="inline-flex items-center">{label}<span className="ml-xs bg-muted flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-[6px] text-[11px] leading-none">{option.counter}</span></span> : label;
    $[4] = label;
    $[5] = option.counter;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const labelContent = t3;
  const buttonSize = SIZE_MAP[size];
  const t4 = fullWidth && "w-full shrink";
  const t5 = vertical && "justify-start";
  const t6 = !isActive && "border-[0.5px] border-transparent";
  const t7 = isActive && "dark:border-transparent dark:bg-secondary dark:shadow-none";
  let t8;
  if ($[7] !== t4 || $[8] !== t5 || $[9] !== t6 || $[10] !== t7) {
    t8 = cn(t4, t5, "transition-none", t6, t7);
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
    $[10] = t7;
    $[11] = t8;
  } else {
    t8 = $[11];
  }
  const className = t8;
  const isDisabled = option.isDisabled ?? false;
  const suppressPointerFocus = _temp;
  if (option.href && option.external && !isDisabled) {
    const t9 = isActive ? "secondary" : "ghost";
    let t10;
    if ($[12] !== onChange || $[13] !== option.value) {
      t10 = () => onChange?.(option.value);
      $[12] = onChange;
      $[13] = option.value;
      $[14] = t10;
    } else {
      t10 = $[14];
    }
    let t11;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = <ArrowDiagonalUp className="ml-2xs size-4 shrink-0" />;
      $[15] = t11;
    } else {
      t11 = $[15];
    }
    let t12;
    if ($[16] !== labelContent || $[17] !== option.href || $[18] !== t10) {
      t12 = <ExternalLink href={option.href} onClick={t10} onPointerDown={suppressPointerFocus}>{labelContent}{t11}</ExternalLink>;
      $[16] = labelContent;
      $[17] = option.href;
      $[18] = t10;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] !== buttonSize || $[21] !== className || $[22] !== t12 || $[23] !== t9) {
      t13 = <Button asChild={true} variant={t9} size={buttonSize} className={className}>{t12}</Button>;
      $[20] = buttonSize;
      $[21] = className;
      $[22] = t12;
      $[23] = t9;
      $[24] = t13;
    } else {
      t13 = $[24];
    }
    return t13;
  }
  if (option.href && !isDisabled) {
    const t9 = isActive ? "secondary" : "ghost";
    const t10 = isActive ? "page" : undefined;
    let t11;
    if ($[25] !== onChange || $[26] !== option.value) {
      t11 = () => onChange?.(option.value);
      $[25] = onChange;
      $[26] = option.value;
      $[27] = t11;
    } else {
      t11 = $[27];
    }
    let t12;
    if ($[28] !== labelContent || $[29] !== option.href || $[30] !== option.prefetch || $[31] !== t10 || $[32] !== t11) {
      t12 = <InternalLink href={option.href} prefetch={option.prefetch} aria-current={t10} onClick={t11} onPointerDown={suppressPointerFocus}>{labelContent}</InternalLink>;
      $[28] = labelContent;
      $[29] = option.href;
      $[30] = option.prefetch;
      $[31] = t10;
      $[32] = t11;
      $[33] = t12;
    } else {
      t12 = $[33];
    }
    let t13;
    if ($[34] !== buttonSize || $[35] !== className || $[36] !== t12 || $[37] !== t9) {
      t13 = <Button asChild={true} variant={t9} size={buttonSize} className={className}>{t12}</Button>;
      $[34] = buttonSize;
      $[35] = className;
      $[36] = t12;
      $[37] = t9;
      $[38] = t13;
    } else {
      t13 = $[38];
    }
    return t13;
  }
  const t9 = isActive ? "secondary" : "ghost";
  let t10;
  if ($[39] !== onChange || $[40] !== option.value) {
    t10 = () => onChange?.(option.value);
    $[39] = onChange;
    $[40] = option.value;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  let t11;
  if ($[42] !== buttonSize || $[43] !== className || $[44] !== isActive || $[45] !== isDisabled || $[46] !== labelContent || $[47] !== t10 || $[48] !== t9) {
    t11 = <Button variant={t9} size={buttonSize} disabled={isDisabled} aria-pressed={isActive} className={className} onClick={t10} onPointerDown={suppressPointerFocus}>{labelContent}</Button>;
    $[42] = buttonSize;
    $[43] = className;
    $[44] = isActive;
    $[45] = isDisabled;
    $[46] = labelContent;
    $[47] = t10;
    $[48] = t9;
    $[49] = t11;
  } else {
    t11 = $[49];
  }
  return t11;
}
function _temp(event) {
  return event.preventDefault();
}